import { compose, pick } from 'ramda';
import React, { useEffect, useState } from 'react';
import { withDynamicReducer, useDynamicReducerDispatch, useDynamicReducerSelector } from './store';

import * as hooks from './hooks';
import Controls from './Controls';
import * as actions from './store/actions';
import SlidesAnimation from './SlidesAnimation';
import ProgressBar from './ProgressBar';

import style from './index.scss';

export const Slider = (props) => {
    const {
        options: {
            controls,
            ...options
        } = {},
        slideWidth,
        children,
        autoRotate = false,
        renderAll = true,
        infinite = autoRotate || false,
        showControls = true,
        showControlsOnHover = false,
        swipe = true,
        className = '',
        classNames = {},
        styles = {},
        refs = {},
        handlerSliderTransitionEnd,
        onActiveSlideChange,
        isBulletsClickable,
        isAllItemsVisible = false,
        showDisabledArrows = false,
        showControlsAtStart = false
    } = props;
    const sliderDispatch = useDynamicReducerDispatch();
    const { activeSlide, prevSlide, direction, slidesCount, isMirrorMode } = useDynamicReducerSelector(pick(['activeSlide', 'prevSlide', 'direction', 'slidesCount', 'isMirrorMode']));
    const [isHover, setIsHover] = useState(false);
    const isAutoRotateDisabled = options.animation?.displayTime === 0;
    const isProgressBarAtTop = controls?.progressBar?.enabled && controls?.progressBar?.position === 'top';
    const isProgressBarAtBottom = controls?.progressBar?.enabled && controls?.progressBar?.position === 'bottom';
    const {
        onMouseEnter,
        onMouseLeave
    } = hooks.useSliderMouseEventHandlers({ slidesCount, showControlsOnHover, setIsHover });

    useEffect(() => {
        sliderDispatch(actions.addSlider({ slidesCount: children.length }));
    }, [children.length, sliderDispatch]);

    hooks.useActiveSlideChangeEffect({ onActiveSlideChange, sliderState: { activeSlide, prevSlide, direction, slidesCount } });

    const doShowControls = showControls && slidesCount > 1 && (showControlsOnHover ? isHover : true);

    if (!slidesCount) {
        return null;
    }

    const controlsContent = (
        <Controls
            options={controls}
            infinite={infinite}
            classNames={classNames}
            activeSlide={activeSlide}
            slidesCount={slidesCount}
            showDisabledArrows={showDisabledArrows}
            isBulletsClickable={isBulletsClickable}
        />
    );

    return (
        <>
            {isProgressBarAtTop && slidesCount > 1 && (
                <ProgressBar classNames={classNames} activeSlide={activeSlide} slidesCount={slidesCount} />
            )}
            <div
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={`${style.component} ${className}`}
            >
                {doShowControls && showControlsAtStart && controlsContent}
                <SlidesAnimation options={{ slideGap: 0, swipeAngleX: 30, ...options }}
                    autoRotate={autoRotate && slidesCount > 1 && !isAutoRotateDisabled}
                    isSwipeEnabled={swipe && slidesCount > 1}
                    slideRef={refs.slide}
                    sliderRef={refs.slider}
                    classNames={classNames}
                    styles={styles}
                    activeSlide={activeSlide}
                    prevSlide={prevSlide}
                    direction={direction}
                    slidesCount={slidesCount}
                    slideWidth={slideWidth}
                    renderAll={renderAll}
                    isMirrorMode={isMirrorMode}
                    handlerSliderTransitionEnd={handlerSliderTransitionEnd}
                    isAllItemsVisible={isAllItemsVisible}
                    infinite={infinite}>
                    {children}
                </SlidesAnimation>

                {doShowControls && !showControlsAtStart && controlsContent}
            </div>
            {isProgressBarAtBottom && slidesCount > 1 && (
                <ProgressBar classNames={classNames} activeSlide={activeSlide} slidesCount={slidesCount} />
            )}
        </>
    );
};

export default compose(
    withDynamicReducer()
)(Slider);
